import React, { useState } from 'react';
import { FileDown } from 'lucide-react';
import api from '@/lib/api';
import { BASE, Box, inp } from './ui';

const hdr = (aluno) => ({ 'X-Acervo-Aluno': encodeURIComponent(aluno) });
const erroMsg = (e) => (e?.response?.status === 403 ? 'Acesso negado: a ficha exige perfil autorizado e a permissão individual acervo.notas_nominais.' : e?.response?.data?.detail || 'Erro na consulta.');
const dash = (v) => (v === null || v === undefined || v === '' || v === '-1' ? '—' : v);
const brl = (v) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const Sec = ({ title, children, testid }) => <div className="mt-4" data-testid={testid}><p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">{title}</p>{children}</div>;
const T = ({ head, rows }) => (
  <table className="w-full text-xs"><thead><tr className="text-left text-slate-500">{head.map((h) => <th key={h} className="pr-2">{h}</th>)}</tr></thead>
    <tbody>{rows.length ? rows.map((r, i) => <tr key={i} className="border-t">{r.map((c, j) => <td key={j} className="py-0.5 pr-2">{dash(c)}</td>)}</tr>) : <tr><td className="text-slate-400">Sem registros no acervo.</td></tr>}</tbody></table>
);

function Ficha({ f, aluno }) {
  const [baixando, setBaixando] = useState(false);
  const pdf = () => {
    setBaixando(true);
    api.get(`${BASE}/ficha/pdf`, { headers: hdr(aluno), responseType: 'blob' }).then(({ data }) => {
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `ficha_historica_${f.aluno.nome.split(' ')[0]}.pdf`; a.click();
      URL.revokeObjectURL(url);
    }).finally(() => setBaixando(false));
  };
  const a = f.aluno;
  return (
    <Box testid="acervo-ficha">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-lg font-bold" data-testid="acervo-ficha-nome">{a.nome}{a.nome_social && <span className="ml-2 text-xs text-slate-500">(nome social: {a.nome_social})</span>}</p>
          <p className="text-xs text-slate-600">Código Sponte {a.aluno_id} · nascimento {dash(a.nascimento)} · sexo {dash(a.sexo)} · CPF {dash(a.cpf)}</p>
        </div>
        <button type="button" onClick={pdf} disabled={baixando} className="flex items-center gap-2 rounded-lg bg-amber-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-800 disabled:opacity-50" data-testid="acervo-ficha-pdf">
          <FileDown className="h-4 w-4" />{baixando ? 'Gerando…' : 'Baixar ficha em PDF'}
        </button>
      </div>
      <Sec title="Turmas por ano" testid="acervo-ficha-turmas"><T head={['Ano', 'Turma', 'Curso', 'Matrícula', 'Transferido']} rows={f.turmas.map((t) => [t.ano, t.turma, t.curso, t.matricula, t.transferido ? 'sim' : 'não'])} /></Sec>
      <Sec title="Notas finais por disciplina e ano" testid="acervo-ficha-notas">
        {Object.keys(f.notas).length ? Object.entries(f.notas).map(([ano, ns]) => (
          <div key={ano} className="mb-2"><p className="text-xs font-semibold">{ano}</p><T head={['Disciplina', 'Média', 'Média final', 'Faltas']} rows={ns.map((n) => [n.disciplina, n.media, n.media_final, n.faltas])} /></div>))
          : <p className="text-xs text-slate-400">Sem notas consolidadas no acervo.</p>}
      </Sec>
      <Sec title="Frequência por ano" testid="acervo-ficha-frequencia"><T head={['Ano', 'Aulas registradas', 'Faltas', 'Justificadas', 'Presença']} rows={f.frequencia.map((x) => [x.ano, x.aulas, x.faltas, x.justificadas, x.presenca_pct != null ? `${x.presenca_pct}%` : null])} /></Sec>
      <Sec title="Responsáveis" testid="acervo-ficha-responsaveis"><T head={['Nome', 'Vínculo', 'Telefone', 'E-mail', 'Papel']} rows={f.responsaveis.map((r) => [r.nome, r.tipo, r.telefone, r.email, [r.financeiro && 'financeiro', r.didatico && 'didático'].filter(Boolean).join(', ')])} /></Sec>
      <Sec title="Resumo financeiro" testid="acervo-ficha-financeiro">
        {f.financeiro_restrito || !f.financeiro ? <p className="text-xs text-slate-500">Seção restrita à Direção.</p>
          : <T head={['Ano (vencimento)', 'Parcelas', 'Valor total', 'Valor pago', 'Em aberto']} rows={f.financeiro.map((x) => [x.ano, x.parcelas, brl(x.valor), brl(x.pago), x.em_aberto])} />}
      </Sec>
      <p className="mt-3 text-[11px] text-slate-500">Valores exatos da origem Sponte (2010–2025). Nada é recalculado.</p>
    </Box>
  );
}

export function FichaTab() {
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState(null);
  const buscar = (e) => {
    e.preventDefault(); setErr(''); setSel(null);
    api.get(`${BASE}/academico/alunos`, { headers: { 'X-Acervo-Busca': encodeURIComponent(q.trim()) }, params: { limite: 50 } })
      .then(({ data }) => setRes([...new Map(data.items.map((r) => [r.aluno_id, r])).values()])).catch((x) => { setRes(null); setErr(erroMsg(x)); });
  };
  const abrir = (r) => {
    setErr('');
    api.get(`${BASE}/ficha`, { headers: hdr(r.aluno_id) }).then(({ data }) => setSel({ aluno: r.aluno_id, f: data })).catch((x) => setErr(erroMsg(x)));
  };
  return (
    <div className="space-y-3">
      <Box testid="acervo-ficha-busca">
        <form onSubmit={buscar} className="flex flex-wrap gap-2">
          <input className={`${inp} w-72`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome do aluno ou código Sponte" data-testid="acervo-ficha-busca-input" />
          <button type="submit" className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm text-white" data-testid="acervo-ficha-buscar">Buscar</button>
        </form>
        {err && <p className="mt-2 text-xs text-rose-700" data-testid="acervo-ficha-erro">{err}</p>}
        {res && (
          <ul className="mt-2 divide-y text-sm" data-testid="acervo-ficha-resultados">
            {res.map((r) => <li key={r.aluno_id}><button type="button" onClick={() => abrir(r)} className="w-full py-1.5 text-left hover:bg-amber-50" data-testid={`acervo-ficha-aluno-${r.aluno_id}`}>
              <span className="font-mono text-xs text-slate-500">{r.aluno_id}</span> · {r.nome}{r.homonimo && <span className="ml-2 rounded bg-amber-200 px-1 text-[10px] font-bold">HOMÔNIMO</span>}</button></li>)}
            {!res.length && <li className="py-1 text-xs text-slate-400">Nenhum aluno encontrado.</li>}
          </ul>)}
      </Box>
      {sel && <Ficha f={sel.f} aluno={sel.aluno} />}
    </div>
  );
}
